import { CSSObject } from "@emotion/react";
import { alpha, Button, ButtonProps, styled } from "@mui/material";
import React, { ReactNode } from "react";
import type { CustomPaletteOptions } from "../../../types/theme";
import { useCapitalization, useShadows } from "../../../builder/Theme";
import { BodySizes } from "../../../builder/useFontsLayout";
import { LinkWrapper } from "../anchor/LinkWrapper";
import {
  INPUT_VERTICAL_PADDING,
  INPUT_SIZES,
} from "../../../../theme/defaultConstants";
import { defaultFocusOutline } from "../../../helpers/accessibility";

export interface Props {
  customVariant?: "secondary";
}

export const ThemeButton = styled(
  React.forwardRef(
    (
      props: ButtonProps & {
        children?: ReactNode;
        target?: string;
      },
      ref: React.Ref<HTMLButtonElement>,
    ) => {
      const href = props?.href || "";
      const nextLink = href && !/^https?:\/\//i.test(href);
      const button = (
        <Button
          variant={props.variant ? props.variant : "contained"}
          ref={ref}
          disableFocusRipple
          {...props}
        />
      );
      return nextLink ? (
        <LinkWrapper href={href} passHref legacyBehavior>
          {button}
        </LinkWrapper>
      ) : (
        button
      );
    },
  ),
)<Props>(({ theme, variant, customVariant }) => {
  const { buttons: config, fonts } = theme.settings;
  const isSecondary =
    customVariant === "secondary" &&
    config.secondaryButtons.useSecondaryButtons;

  const buttons = isSecondary ? config.secondaryButtons : config;

  const paletteKey: keyof CustomPaletteOptions = isSecondary
    ? "secondaryButtons"
    : "buttons";

  const textTransform = useCapitalization(buttons?.capitalization);
  const boxShadow = useShadows(buttons?.shadows);

  const minHeight =
    (INPUT_SIZES[buttons.size] || INPUT_SIZES.medium) + INPUT_VERTICAL_PADDING;

  const variants: Partial<
    Record<"text" | "outlined" | "contained", CSSObject>
  > = {
    text: {
      color: theme.palette[paletteKey].main,
      fontWeight: `${700}!important`,
    },
    contained: {
      background: theme.palette[paletteKey].main,
      color: theme.palette[paletteKey].contrastText,
      borderColor: alpha(
        theme.palette[`${paletteKey}Outline`].main,
        buttons.border.opacity,
      ),
      "&:hover": {
        background: theme.palette[paletteKey].dark,
      },
    },
    outlined: {
      color: theme.palette[`${paletteKey}Outline`].main,
      borderColor: alpha(
        theme.palette[`${paletteKey}Outline`].main,
        buttons.border.opacity,
      ),
      "&:hover": {
        background: alpha(theme.palette[paletteKey].main, 0.03),
      },
    },
  };

  const variantStyles = variants[variant || "contained"];

  return {
    minHeight,
    padding: `${theme.spacing(0.6)} ${theme.spacing(2)}`,
    fontSize:
      theme.typography[BodySizes[fonts.buttons?.size || "medium"]].fontSize,
    textTransform,
    boxShadow,
    borderStyle: "solid",
    borderWidth: `${buttons.border.thickness}px`,
    borderRadius: buttons.border.radius * 5,
    ...variantStyles,
    "&:disabled": {
      background: theme.palette.grey[300],
      color: theme.palette.grey[500],
      border: "none",
    },
    "&.Mui-focusVisible": {
      outline: defaultFocusOutline(theme),
    },
  };
});
